import { useEffect, useMemo, useRef, useState } from 'react'

import { debounce } from 'lodash'

import { toast } from '@webapp/components/Notification'
import { type UserSelectOption } from '@webapp/components/UserSelect'
import { getApiDetailMessage } from '@webapp/core/utils/errors/getApiDetailMessage'
import { useProcurifyIntl } from '@webapp/hooks'
import { useMutatePOBillToContact } from '@webapp/modules/purchasing/features/set-po-bill-to-settings/models/useMutatePOBillToContact'
import { useMutatePOBillToPrimaryCategory } from '@webapp/modules/purchasing/features/set-po-bill-to-settings/models/useMutatePOBillToPrimaryCategory'
import {
  POBillToContactModes,
  type POBillToContactSystemPreference,
  type POBillToPrimaryCategorySystemPreference,
} from '@webapp/platform/iam'
import messages from './messages'

const SAVE_DEBOUNCE_MS = 500

export interface IUsePOBillToSettingsPresenterProps {
  primaryCategory: POBillToPrimaryCategorySystemPreference['value']
  contact: POBillToContactSystemPreference['value']
  // The stored specific user, in the shape the picker needs (a UserSummary fits)
  contactUser: Nullable<UserSelectOption>
  isLoadingContactUser: boolean
}

/**
 * Optimistic values, a 500ms-debounced save per setting and revert-on-failure,
 * mirroring useExternalPONumberingTogglePresenter. Saved values arrive as
 * props; this command feature never queries.
 *
 * Each sync effect applies incoming props only while no local edit of that
 * setting is unsaved, so a refetch cannot undo a pending save. A plain flag
 * per setting is enough: lodash.debounce coalesces the edits made inside the
 * window into one save, and the control is disabled while that save is in
 * flight, so no edit can land between a save invoking and settling. The
 * contact sync also requires `contactUser`'s id to match `contact.userId` (a
 * failed by-id lookup reports null with the id still set), so it never hides
 * the picker.
 */
export const usePOBillToSettingsPresenter = ({
  primaryCategory,
  contact,
  contactUser,
  isLoadingContactUser,
}: IUsePOBillToSettingsPresenterProps) => {
  const intl = useProcurifyIntl()
  const {
    mutateAsync: mutatePrimaryCategory,
    isLoading: isSavingPrimaryCategory,
  } = useMutatePOBillToPrimaryCategory()
  const { mutateAsync: mutateContact, isLoading: isSavingContact } =
    useMutatePOBillToContact()

  const [primaryCategoryValue, setPrimaryCategoryValue] =
    useState(primaryCategory)
  const savedPrimaryCategoryRef = useRef(primaryCategory)
  const hasUnsavedPrimaryCategoryEditRef = useRef(false)

  const [contactValue, setContactValue] = useState(contact)
  const savedContactRef = useRef(contact)
  const [selectedContactUser, setSelectedContactUser] =
    useState<Nullable<UserSelectOption>>(contactUser)
  const savedContactUserRef = useRef<Nullable<UserSelectOption>>(contactUser)
  const hasUnsavedContactEditRef = useRef(false)

  useEffect(() => {
    if (hasUnsavedPrimaryCategoryEditRef.current) return

    savedPrimaryCategoryRef.current = primaryCategory
    setPrimaryCategoryValue(primaryCategory)
  }, [primaryCategory])

  useEffect(() => {
    if (isLoadingContactUser) return
    if (hasUnsavedContactEditRef.current) return
    if ((contactUser?.id ?? null) !== contact.userId) return

    savedContactRef.current = contact
    savedContactUserRef.current = contactUser
    setContactValue(contact)
    setSelectedContactUser(contactUser)
  }, [contact, contactUser, isLoadingContactUser])

  const intlRef = useRef(intl)
  intlRef.current = intl

  const mutatePrimaryCategoryRef = useRef(mutatePrimaryCategory)
  mutatePrimaryCategoryRef.current = mutatePrimaryCategory

  const mutateContactRef = useRef(mutateContact)
  mutateContactRef.current = mutateContact

  const debouncedSavePrimaryCategory = useMemo(
    () =>
      debounce(
        async (next: POBillToPrimaryCategorySystemPreference['value']) => {
          try {
            await mutatePrimaryCategoryRef.current(next)
            savedPrimaryCategoryRef.current = next

            toast.success({
              title: intlRef.current.formatMessage(
                messages.primaryCategorySuccessTitle
              ),
              children: intlRef.current.formatMessage(
                messages.primaryCategorySuccessMessage
              ),
            })
          } catch (error) {
            setPrimaryCategoryValue(savedPrimaryCategoryRef.current)

            toast.error({
              title: intlRef.current.formatMessage(messages.errorTitle),
              children:
                getApiDetailMessage(error) ??
                intlRef.current.formatMessage(messages.errorMessage),
            })
          } finally {
            hasUnsavedPrimaryCategoryEditRef.current = false
          }
        },
        SAVE_DEBOUNCE_MS
      ),
    []
  )

  const debouncedSaveContact = useMemo(
    () =>
      debounce(
        async (
          next: POBillToContactSystemPreference['value'],
          nextUser: Nullable<UserSelectOption>
        ) => {
          try {
            await mutateContactRef.current(next)
            savedContactRef.current = next
            savedContactUserRef.current = nextUser

            toast.success({
              title: intlRef.current.formatMessage(
                messages.contactSuccessTitle
              ),
              children: intlRef.current.formatMessage(
                messages.contactSuccessMessage
              ),
            })
          } catch (error) {
            setContactValue(savedContactRef.current)
            setSelectedContactUser(savedContactUserRef.current)

            toast.error({
              title: intlRef.current.formatMessage(messages.errorTitle),
              children:
                getApiDetailMessage(error) ??
                intlRef.current.formatMessage(messages.errorMessage),
            })
          } finally {
            hasUnsavedContactEditRef.current = false
          }
        },
        SAVE_DEBOUNCE_MS
      ),
    []
  )

  useEffect(
    () => () => {
      debouncedSavePrimaryCategory.cancel()
      debouncedSaveContact.cancel()
    },
    [debouncedSavePrimaryCategory, debouncedSaveContact]
  )

  // react-select fires onChange for a single select even when the clicked
  // option is already selected; those re-selections are not edits
  const onPrimaryCategoryChange = (
    next: POBillToPrimaryCategorySystemPreference['value']
  ) => {
    if (next === primaryCategoryValue) return

    hasUnsavedPrimaryCategoryEditRef.current = true
    setPrimaryCategoryValue(next)
    void debouncedSavePrimaryCategory(next)
  }

  const onContactModeChange = (
    next: POBillToContactSystemPreference['value']['mode']
  ) => {
    if (next === contactValue.mode) return

    hasUnsavedContactEditRef.current = true

    if (next === POBillToContactModes.SPECIFIC_USER) {
      setContactValue({ mode: next, userId: null })
      // Nothing is saved until a user is chosen, and a save queued from the
      // previous mode must not land under the unsaved "Specific user" choice
      debouncedSaveContact.cancel()
      return
    }

    setContactValue({ mode: next, userId: null })
    setSelectedContactUser(null)
    void debouncedSaveContact({ mode: next, userId: null }, null)
  }

  const onContactUserChange = (user: Nullable<UserSelectOption>) => {
    if ((user?.id ?? null) === (selectedContactUser?.id ?? null)) return

    hasUnsavedContactEditRef.current = true

    setSelectedContactUser(user)
    setContactValue({
      mode: POBillToContactModes.SPECIFIC_USER,
      userId: user?.id ?? null,
    })

    if (user) {
      void debouncedSaveContact(
        {
          mode: POBillToContactModes.SPECIFIC_USER,
          userId: user.id,
        },
        user
      )
    } else {
      debouncedSaveContact.cancel()
    }
  }

  const isContactUserRequired =
    contactValue.mode === POBillToContactModes.SPECIFIC_USER &&
    contactValue.userId == null

  return {
    states: {
      primaryCategory: primaryCategoryValue,
      isSavingPrimaryCategory,
      contactMode: contactValue.mode,
      selectedContactUser,
      isContactUserRequired,
      isSavingContact,
    },
    methods: {
      onPrimaryCategoryChange,
      onContactModeChange,
      onContactUserChange,
    },
  }
}
